import React, { useState, useRef, useEffect } from 'react';
import { Plus, X } from 'lucide-react';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '😡'];

// Full emoji list for custom reaction (same categories as EmojiPickerCustom)
const ALL_EMOJIS: string[] = [
  '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩',
  '😘','😗','☺️','😚','😙','🥲','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔',
  '🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷',
  '🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐',
  '😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭',
  '😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️',
  '💩','🤡','👹','👺','👻','👽','👾','🤖',
  '👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆',
  '👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏',
  '💪','👀','👅','💋','💔','❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎',
  '❤️‍🔥','❤️‍🩹','💕','💞','💓','💗','💖','💘','💝','💟',
  '🔥','✨','💫','⭐','🌟','💥','🎉','🎊','🎈','🎁','🏆','🥇','🎯',
  '😺','😸','😹','😻','😼','😽','🙀','😿','😾',
  '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵',
  '🍎','🍊','🍋','🍇','🍓','🍔','🍕','🌮','🍦','🍩','🍪','🎂','🍫','🍬','🍭',
  '⚽','🏀','🏈','🎾','🎮','🕹️','🎲','♟️','🎭','🎨','🎵','🎶','🎤','🎧',
  '🚗','✈️','🚀','🛸','⛵','🏠','🏖️','🌍','🌈','☀️','🌙','⭐','❄️','🌊',
  '💡','🔑','🔒','💎','💰','💳','📱','💻','📷','🎥','📚','✏️','📝','🗓️',
];

interface Props {
  onReact: (emoji: string) => void;
  onClose: () => void;
  position: { x: number; y: number };
}

const ReactionPicker: React.FC<Props> = ({ onReact, onClose, position }) => {
  const [showFull, setShowFull] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Focus search when full picker opens
  useEffect(() => {
    if (showFull) setTimeout(() => searchRef.current?.focus(), 50);
  }, [showFull]);

  const filteredEmojis = search.trim()
    ? ALL_EMOJIS.filter(e => e.includes(search))
    : ALL_EMOJIS;

  // Smart positioning: keep picker inside viewport
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - (showFull ? 280 : 260)),
    top: Math.max(position.y - (showFull ? 320 : 60), 8),
    zIndex: 9999,
  };

  return (
    <div ref={ref} className="rp-wrap" style={style} onClick={e => e.stopPropagation()}>
      {!showFull ? (
        /* ── Quick bar ── */
        <div className="rp-bar">
          {QUICK_REACTIONS.map(emoji => (
            <button key={emoji} className="rp-bar__btn" onClick={() => { onReact(emoji); onClose(); }}>
              {emoji}
            </button>
          ))}
          <button className="rp-bar__plus" onClick={() => setShowFull(true)} title="Más emojis">
            <Plus size={16} />
          </button>
        </div>
      ) : (
        /* ── Full emoji picker ── */
        <div className="rp-full">
          <div className="rp-full__header">
            <span className="rp-full__title">Reaccionar</span>
            <button className="rp-full__close" onClick={onClose}><X size={14} /></button>
          </div>

          <div className="rp-full__search-wrap">
            <input
              ref={searchRef}
              className="rp-full__search"
              placeholder="Buscar emoji..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Quick reactions row at top */}
          {!search && (
            <div className="rp-full__quick">
              {QUICK_REACTIONS.map(emoji => (
                <button key={emoji} className="rp-full__btn" onClick={() => { onReact(emoji); onClose(); }}>
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <div className="rp-full__grid">
            {filteredEmojis.map((emoji, i) => (
              <button key={i} className="rp-full__btn" onClick={() => { onReact(emoji); onClose(); }}>
                {emoji}
              </button>
            ))}
            {filteredEmojis.length === 0 && (
              <div className="rp-full__empty">Sin resultados</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactionPicker;
export { ReactionPicker };